import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CustomSelect from '../FormInputs/CustomSelect/CustomSelect';
import Button from '../Button/Button';
import { importOptions } from '../../utils/imports';
import HeaderIconTooltip from '../HeaderIconTooltip/HeaderIconTooltip';
import HypertextLink from '../HypertextLink/HypertextLink';
import { capitalizeFirstLetter } from '../../utils/api';
import { importFile, getImportStatus } from '../../utils/api/imports';
import { useToasts } from '../../hooks';
import { redirectToImport } from '../../utils/navigation';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import DragDropFiles from '../DragAndDrop/DragDropFiles';

const ImportPicker = ({ organizationId, setIsNewImport, customError }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [activeImport, setActiveImport] = useState(importOptions[0]);
  const [isFileOverBox, setIsFileOverBox] = useState(false);
  const [droppedFile, setDroppedFile] = useState(null);
  const [isValidExtension, setIsValidExtension] = useState(false);
  const [isImportInProcess, setIsImportInProcess] = useState(false);
  const { showToast } = useToasts();
  const { setToLocalStorage, removeFromLocalStorage, getFromLocalStorage } = useLocalStorage();

  // handle import button disable property
  useEffect(() => {
    if (droppedFile && droppedFile.name && isValidExtension) {
      setIsDisabled(false);
    }
  }, [droppedFile, isValidExtension]);

  // Reusable function to validate file type and set state
  const validateAndSetFile = (file) => {
    setDroppedFile(file);
    // validate file type
    const isValidFileType = file.type === 'text/csv';
    // in case browser do not support file.type as expected
    const eventFileNameExtension = file.name.toLowerCase().endsWith('.csv');
    setIsValidExtension(isValidFileType || eventFileNameExtension);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (droppedFile) {
      return;
    }

    // droppedFiles is a FileList object from the event
    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles.length === 0) return;
    // handle dropped file/files.
    // If dropped more than 1 file - handle first file in the array
    const newFiles = Array.from(droppedFiles);
    // if user drops more than one file, only take the first one
    validateAndSetFile(newFiles[0]);
  };

  const handleAddFiles = (event) => {
    const { files } = event.target;
    if (files.length > 0) {
      const newFiles = Array.from(files);
      // even our input allows only one file, we still receive a file array in the event
      validateAndSetFile(newFiles[0]);
    }
  };

  const handleSubmit = () => {
    // change the button name to "Importing..." and disable it
    setIsImportInProcess(true);
    setIsDisabled(true);
    setIsFileOverBox(false);
    importFile(organizationId, activeImport.type, droppedFile)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText || customError);
        }
        return response.json();
      })
      .then((data) => {
        if (!data) {
          throw new Error(data.statusText || customError);
        }
        const importId = data.import_file.id;
        setToLocalStorage('pendingImportId', importId);
        getImportStatus({
          importId, customError,
        })
          .then((importData) => {
            if (!importData) {
              throw new Error(customError);
            }
            // If pendingImportId is gone, PageHeaderWrapper already handled this import — skip to avoid double toast.
            if (!getFromLocalStorage('pendingImportId')) return;

            if (importData.status === 'Complete with Errors') {
              // set the error message to local storage to show it after redirection
              setToLocalStorage('toastMessageForError', customError);
              redirectToImport(importId);
            }
            if (importData.status === 'Complete') {
              showToast({ message: 'Your data has been successfully imported.', type: 'success' });
            }
            setIsNewImport(true);
          })
          .catch((error) => {
            showToast({ message: error.message || customError, type: 'error' });
          })
          .finally(() => {
            removeFromLocalStorage('pendingImportId');
            setIsImportInProcess(false);
            setDroppedFile(null);
            setIsValidExtension(false);
          });
      })
      .catch((error) => {
        showToast({ message: error.message || customError, type: 'error' });
        setIsImportInProcess(false);
        setDroppedFile(null);
        setIsValidExtension(false);
      });
  };

  const handleSelectChange = (selectedOption) => {
    setActiveImport(selectedOption);
  };

  const redirectionButtons = [
    {
      label: `${capitalizeFirstLetter(activeImport.type)} template`,
      url: activeImport.templateUrl,
    },
    {
      label: `${capitalizeFirstLetter(activeImport.type)} terms`,
      url: activeImport.termUrl,
    },
  ];

  const redirectToForButtons = (url) => window.open(url, '_blank');

  const sectionClasses = 'flex flex-col ml-2 my-2.5 gap-y-4';
  const mainDivInSectionClasses = 'flex items-top gap-1';

  return (
    <div
      className="w-1/3 h-fit bg-vamos-blue2 bg-opacity-10 rounded-10px p-5.5 min-w-97.25"
    >
      <div className="flex flex-col gap-5.5 pb-28">
        <h2 className="text-2xl font-bold">New Import</h2>
        {/* section 1 - type of import */}
        <section className={sectionClasses} id="import-picker" data-testid="import-picker">
          <div className={mainDivInSectionClasses}>
            <p className="text-lg">1.</p>
            <p className="text-lg">What would you like to import?</p>
          </div>
          <div className="pl-4">
            <CustomSelect
              value={activeImport.value}
              options={importOptions}
              setValue={handleSelectChange}
              placeholder="Select"
              name="import_name"
              dataTestId="select-import-type"
            />
          </div>
        </section>
        {/* section 2- templates */}
        <section className={sectionClasses} id="import-example" data-testid="import-example">
          <div className={mainDivInSectionClasses}>
            <p className="text-lg">2.</p>
            <p className="text-lg">{activeImport.helpText}</p>
          </div>
          <div className="pl-3 space-y-4.75">
            {redirectionButtons.map((button) => (
              <Button
                key={button.label}
                label={button.label}
                fontWeight="font-medium"
                color="tertiary-notuppercase"
                size="md-full"
                onClick={() => redirectToForButtons(button.url)}
                dataTestId={`redirect-to-${button.label.toLowerCase().replace(/\s+/g, '-')}`}
              />
            ))}
          </div>
        </section>
        {/* section 3- select file */}
        <section className={sectionClasses} id="import-file-upload" data-testid="import-file-upload">
          <div className={mainDivInSectionClasses}>
            <p className="text-lg">3.</p>
            <p className="text-lg">Select your file.</p>
          </div>
          <DragDropFiles
            droppedFile={droppedFile}
            setDroppedFile={setDroppedFile}
            isValidExtension={isValidExtension}
            setIsValidExtension={setIsValidExtension}
            setIsDisabled={setIsDisabled}
            setIsFileOverBox={setIsFileOverBox}
            handleDrop={handleDrop}
            handleAddFiles={handleAddFiles}
            isFileOverBox={isFileOverBox}
            dropBoxLabel="Choose a CSV File"
            allowedFileTypes={['.csv']}
            dragDropBoxHeight="h-21.25"
            dataTestId="import-page"
          />
        </section>
        {/* section 4- import data */}
        <section className={sectionClasses} id="import-data-button" data-testid="import-data-button">
          <div className={mainDivInSectionClasses}>
            <p className="text-lg">4.</p>
            <p className="text-lg">Import your data.</p>
            <HeaderIconTooltip
              heading=""
              spanTextStart="Most files take less than 2 minutes to import.
              Larger files can take longer.
              You can safely leave this page during upload.
              A confirmation will appear when the upload is complete."
              iconName="icon_circle"
              margins=""
              border=""
              paddingBottom=""
              tooltipId="import-your-data-tooltip"
              iconPosition="items-top"
              tooltipClassName="!max-w-xs"
              dataTestId="import-tooltip-description"
            />

          </div>
          <div className="pl-3 space-y-4.75">
            <Button label={isImportInProcess ? 'Importing...' : 'Import'} isDisabled={isDisabled} onClick={handleSubmit} size="md" fontWeight="font-medium" dataTestId="import-file" />
          </div>
        </section>
      </div>
      <div className="py-2.5">
        <HypertextLink
          className="text-lg underline"
          target="_blank"
          href="https://docs.google.com/document/d/1wJijOJokFYzAdikGchZh1rD_qg3Wb2Yibpz8Aju8Zpg/edit?tab=t.0#heading=h.3x65tym4mnqr"
          dataTestId="import-instructions"
        >Need help? View detailed instructions
        </HypertextLink>
      </div>
    </div>
  );
};

ImportPicker.propTypes = {
  organizationId: PropTypes.number.isRequired,
  setIsNewImport: PropTypes.func.isRequired,
  customError: PropTypes.string.isRequired,
};

export default ImportPicker;
